// Shared staff-portal UI primitives.
//
// Extracted so every tab renders status, loading, empty, error, buttons and
// toasts the same way — matching the bar set by the Withdrawals tab. Before
// this, each tab hand-rolled these with raw Tailwind palette colors, bare
// "Loading…" text, and color-only status. Import from here instead.
//
// Brand note: the marketing `btn-primary`/`btn-secondary` classes are large
// rounded pills meant for hero CTAs; StaffButton below is the compact in-app
// equivalent on the same brand tokens.
//
// Control grammar (2026-07-17): PILLS = value filters (FilterPill /
// SegmentedControl — status filters, scopes, range presets, method pickers);
// UNDERLINE TABS = view switching (Withdrawals' and People's section tabs).
// Don't hand-roll either shape — import from here.
//
// Muted text rule (2026-07-17): muted = `text-on-surface-variant` at FULL
// opacity — never opacity-fade text (/60, /70) below AA contrast. If a value
// needs an "unset/secondary" cue beyond the muted color, use italic or
// wording, not dimness. Type comes from the staff scale in tailwind.config.js
// (title / card-title / ui / context / meta / caps); nothing under 12px except
// caps labels. Portal-wide rules: DESIGN.md § Staff portal.
//
// Touch floor (2026-11-03): every control is at least 44px tall on touch
// screens. Compact desktop rows relax it with `fine:min-h-0` (sm+ AND a mouse
// pointer — the `fine` variant in tailwind.config.js), never `sm:min-h-0`,
// which used to shrink targets on tablets and landscape phones too.

import React from 'react';
import { Link } from 'react-router-dom';
import { timeAgo, fmtDateTimeET } from './dates';
import { UNDO_WINDOW_SECONDS } from './actionGuards';

// ── TimeAgo ───────────────────────────────────────────────────────────────────
// Relative timestamp ("15h ago") that reveals the exact moment — "Jul 21, 2026,
// 7:42 PM ET" (fmtDateTimeET, the one portal ET datetime helper) — on desktop
// hover via the native title tooltip. Use this instead of a bare timeAgo() call
// anywhere the reader might want the real date/time.
//
// tapReveal: opt-in for touch surfaces (Messages / team-Chat message bubbles)
// where hover is unreachable. It renders the label as a chrome-stripped toggle
// button: tap/click swaps the relative label for the exact ET timestamp, tap
// again collapses back — per-instance local state. The hover title is kept in
// BOTH modes, so desktop hover and touch tap reveal the SAME string from the
// SAME helper (one source of truth). Left OFF by default because most call sites
// render TimeAgo INSIDE a clickable row/Link (thread list, quick-jump) where a
// nested button would be invalid and swallow the row's own tap.
export function TimeAgo({ at, className = '', tapReveal = false }) {
  const [shown, setShown] = React.useState(false);
  if (!at) return null;
  const label = timeAgo(at);
  if (!label) return null;
  const exact = fmtDateTimeET(at);
  if (!tapReveal) {
    return (
      <span title={exact} className={className ? `cursor-default ${className}` : 'cursor-default'}>
        {label}
      </span>
    );
  }
  return (
    <button
      type="button"
      title={exact}
      aria-expanded={shown}
      aria-label={shown ? undefined : `Show exact time, ${exact}`}
      onClick={() => setShown((v) => !v)}
      className={`inline-flex items-center min-h-[24px] px-1 py-0.5 bg-transparent border-0 text-inherit text-left cursor-pointer rounded hover:opacity-80 active:opacity-70 motion-safe:transition-opacity ${className}`}
    >
      {shown ? exact : label}
    </button>
  );
}

// ── Opus1 person identity (shared across tabs) ────────────────────────────────
// One canonical way for every staff tab (Time Off, Withdrawals, Tasks, Action
// Center, Approvals) to deep-link a person into the EMBEDDED Opus1 tab —
// /staff/opus1?open=clients/<id>. Before this, four tabs hand-rolled the exact
// same OPUS1_CLIENT_ID_RE + inline <Link>, so a change to the deep-link shape
// had to be made in four places. The person key is the Opus1 client UUID
// (people_id) — the durable identity that lets the desk pull "everything about
// this family" in one place (their whole Opus1 profile: siblings, invoices,
// lessons, notes). StaffOpus1.jsx validates the param strictly (OPEN_PATH_RE);
// we only ever build a link from an id that already looks like an Opus1 uuid.
const OPUS1_CLIENT_ID_RE = /^[0-9a-fA-F-]{6,}$/;

// Extract a usable Opus1 client id from a value or a row. Accepts a raw id
// string, or an object carrying any of the id fields the various tables use
// (opus1_client_id / client_opus1_id / opus1ClientId) or an absolute
// /clients/<id> url (older coverage payloads). Returns null when there's no
// valid id — callers then render plain text (graceful degradation).
export function opus1ClientId(src) {
  if (!src) return null;
  let raw = null;
  if (typeof src === 'string') {
    raw = src;
  } else if (typeof src === 'object') {
    raw =
      src.opus1_client_id ||
      src.client_opus1_id ||
      src.opus1ClientId ||
      (String(src.url || '').match(/\/clients\/([^/?#]+)/) || [])[1] ||
      null;
  }
  raw = raw != null ? String(raw) : null;
  return raw && OPUS1_CLIENT_ID_RE.test(raw) ? raw : null;
}

export function opus1ProfileTo(id, kind = 'client') {
  const segment = kind === 'prospect' ? 'prospects' : 'clients';
  return `/staff/opus1?open=${segment}/${encodeURIComponent(id)}`;
}

// Deep-link a time-off request (staff_timeoff.opus1_id) to its Opus1 page,
// /timeoff/<id>/edit — the only per-request URL that renders. Null when the id
// isn't Opus1-id shaped, so callers render no link.
export function opus1TimeOffTo(id) {
  const raw = typeof id === 'string' ? id : '';
  return OPUS1_CLIENT_ID_RE.test(raw)
    ? `/staff/opus1?open=timeoff/${encodeURIComponent(raw)}/edit`
    : null;
}

// Deep-link a person into the embedded Opus1 tab. `id` is an Opus1 client uuid
// (or pass `source` — a row/object — to have it resolved via opus1ClientId).
// When there's no valid id it renders `fallback` (default: `children` as plain
// text), so a name always shows even when the profile link can't.
//   variant="name"  → the person's name styled as a subtle inline link (default)
//   variant="chip"  → a compact "Open in Opus1 ↗" affordance
// kind="timeoff" links `id` (a time-off request id) via opus1TimeOffTo.
// ariaLabel (chip only) names the target when many chips share one screen.
//
// Interaction safety (2026-07-07): many names sit INSIDE tappable cards/rows
// (thread rows, task rows with expanded hit areas, cards with two-tap confirms).
// Both variants stopPropagation on click so tapping the name never fires the
// surrounding row's action — and the row's tap zone keeps working around it.
// The name variant carries a faint dotted underline so it reads as tappable on
// touch (no hover), while still reading as a name, not a button.
const stopRowTap = (e) => e.stopPropagation();

export function Opus1Link({
  id,
  source,
  kind = 'client',
  variant = 'name',
  className = '',
  title,
  ariaLabel,
  children,
  fallback,
}) {
  const clientId = id ? opus1ClientId(id) : opus1ClientId(source);
  const href = !clientId ? null
    : kind === 'timeoff' ? opus1TimeOffTo(clientId)
    : kind === 'staff' ? `/staff/directory/${encodeURIComponent(clientId)}`
    : opus1ProfileTo(clientId, kind);
  if (!href) {
    return <>{fallback !== undefined ? fallback : children ?? null}</>;
  }
  if (variant === 'chip') {
    return (
      <Link
        to={href}
        onClick={stopRowTap}
        className={`inline-flex items-center gap-0.5 text-primary hover:underline underline-offset-2 whitespace-nowrap ${className}`}
        title={title || 'Open in Opus1'}
        aria-label={ariaLabel}
      >
        {children || 'Open in Opus1'} <span aria-hidden="true">↗</span>
      </Link>
    );
  }
  return (
    <Link
      to={href}
      onClick={stopRowTap}
      className={`text-on-surface underline decoration-dotted decoration-on-surface-variant/50 underline-offset-2 hover:text-primary hover:decoration-primary ${className}`}
      title={title || 'Open in Opus1'}
    >
      {children}
    </Link>
  );
}

// ── Fetch cache-defeat (iOS PWA) ──────────────────────────────────────────────
// The installed iOS PWA (WebKit) aggressively caches GETs, so a "Refresh" tap or
// a re-focus can serve stale data. Every data tab defeats it the same way:
// cache:'no-store' + a no-cache header + a unique ?t= cache-bust per call (the
// server also sends a no-store response header). Previously copy-pasted into
// StaffTimeOff / StaffFrontDesk / StaffDashboard; shared here so every tab reads
// the same and Withdrawals stops being the one tab that drifts stale.
export const NO_STORE = {
  credentials: 'same-origin',
  cache: 'no-store',
  headers: { 'Cache-Control': 'no-cache' },
};
export const bust = (url) => `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`;

// ── useVisibilityRefetch ──────────────────────────────────────────────────────
// The front desk leaves a tab open all day, so a fetch-on-mount-only tab silently
// drifts stale. Re-run `load` whenever the tab becomes visible again — the same
// treatment Approvals + Time Off already use. Pair with NO_STORE/bust on the
// fetch itself so the refetch actually pulls fresh bytes.
export function useVisibilityRefetch(load) {
  React.useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);
}

// ── StatusChip ──────────────────────────────────────────────────────────────
// One chip for every status across the portal. `tone` picks the color, and a
// leading dot gives a non-color cue so the meaning survives for colorblind
// users (color is never the only signal).
//
// The five meanings (Design Wave 3, owner-approved Iris design):
//   neutral  grey          normal work, counts, "Front desk"
//   waiting  teal tint     needs Mark's decision — calm, not an alarm
//   warn     soft coral    getting close to its deadline
//   urgent   solid coral   past the deadline, or a failure
//   success  teal          done and proven (success tokens are teal inside
//                          the staff portal — src/index.css)
// `info` and `danger` are the older names still used across the portal (and
// by the teacher portal, which shares this chip): info = teal-tinted
// in-progress, danger = the failure tint. Inside the staff portal (under
// <StaffChipScope>, mounted by StaffLayout) `danger` renders as the coral
// `urgent` tone — Mark 2026-09-28: one coral for "this is wrong/late" across
// staff screens. The teacher portal has no scope and keeps the red tint.
// Callers keep passing tone="danger"; only the rendering changes. Tones ride
// the semantic tokens in tailwind.config.js — never gold/amber.
const TONES = {
  neutral: { chip: 'bg-surface-container text-on-surface-variant', dot: 'bg-on-surface-variant' },
  waiting: { chip: 'bg-primary-fixed text-on-primary-fixed-variant', dot: 'bg-primary' },
  info: { chip: 'bg-primary/10 text-primary', dot: 'bg-primary' },
  success: { chip: 'bg-success-container text-on-success-container', dot: 'bg-success' },
  warn: { chip: 'bg-warn-container text-on-warn-container', dot: 'bg-warn' },
  urgent: { chip: 'bg-secondary text-on-secondary', dot: 'bg-on-secondary' },
  danger: { chip: 'bg-danger-container text-on-danger-container', dot: 'bg-danger' },
};
export const CHIP_TONES = Object.freeze(Object.keys(TONES));

// Staff-scoped tone remap (see above). Only `danger` moves; every other tone
// renders identically in both portals.
const STAFF_TONE_ALIAS = Object.freeze({ danger: 'urgent' });
const ChipScopeContext = React.createContext(null);

export function StaffChipScope({ children }) {
  return <ChipScopeContext.Provider value="staff">{children}</ChipScopeContext.Provider>;
}

export function StatusChip({ tone = 'neutral', dot = true, uppercase = false, className = '', children, ...rest }) {
  const scope = React.useContext(ChipScopeContext);
  const resolved = scope === 'staff' ? STAFF_TONE_ALIAS[tone] || tone : tone;
  const t = TONES[resolved] || TONES.neutral;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-meta font-label whitespace-nowrap ${
        uppercase ? 'uppercase tracking-wider' : ''
      } ${t.chip} ${className}`}
      {...rest}
    >
      {dot && <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${t.dot}`} aria-hidden="true" />}
      {children}
    </span>
  );
}

// ── Unified display-status vocabulary ─────────────────────────────────────────
// Both action stores use their own raw status strings — agent_actions has a
// 6-state machine (proposed·approved·rejected·executing·done·failed), front_desk
// _tasks has two (open·done). Rather than schema-merge them (which would mean
// row-level gating on the safety-critical agent store), this is a DISPLAY-ONLY
// map: raw status → { label, tone } on the same chip vocabulary every other tab
// already reads. The Action Center feed renders both stores' cards through this
// so "Waiting", "Done", "Failed" mean the same thing regardless of which store a
// row came from. The underlying lifecycles and APIs are untouched.
//
//   <StatusChip {...statusDisplay('approvals', a.status)} />
//   <StatusChip {...statusDisplay('tasks', t.status)} />
const DISPLAY_STATUS = {
  approvals: {
    proposed: { label: 'Needs attention', tone: 'warn' },
    approved: { label: 'Sending', tone: 'info' },
    executing: { label: 'Sending', tone: 'info' },
    done: { label: 'Done', tone: 'success' },
    failed: { label: 'Failed', tone: 'danger' },
    rejected: { label: 'Rejected', tone: 'neutral' },
  },
  tasks: {
    open: { label: 'Open', tone: 'warn' },
    done: { label: 'Done', tone: 'success' },
  },
};

// Resolve a raw status from a given store to its unified display {label, tone,
// key}. Unknown values degrade to a neutral chip showing the raw string, so a
// new backend status never renders blank. `key` is the normalized bucket
// (waiting | in_progress | done | failed | rejected) for cross-store grouping.
const DISPLAY_KEY = {
  proposed: 'needs_attention', open: 'waiting',
  approved: 'sending', executing: 'sending',
  refresh_required: 'sending', awaiting_claim: 'sending',
  needs_attention: 'needs_attention', sending: 'sending',
  done: 'done',
  failed: 'failed',
  rejected: 'rejected',
};
export function statusDisplay(store, raw) {
  const s = String(raw || '').toLowerCase();
  const hit = (DISPLAY_STATUS[store] || {})[s];
  return {
    tone: hit ? hit.tone : 'neutral',
    children: hit ? hit.label : (raw || '—'),
    key: DISPLAY_KEY[s] || 'other',
  };
}

// ── Spinner ───────────────────────────────────────────────────────────────────
// Inline busy indicator for buttons and status lines (border-spinner on
// currentColor, so it inherits the surrounding text color). Decorative —
// always pair it with visible text ("Checking…", "Redrafting…"); aria-hidden
// keeps it silent for screen readers, and motion-safe: parks it for
// prefers-reduced-motion users. For full-region loading keep using <Loading>.
export function Spinner({ size = 'sm', className = '' }) {
  const dim = size === 'md' ? 'w-4 h-4' : 'w-3 h-3';
  return (
    <span
      className={`inline-block ${dim} shrink-0 rounded-full border-2 border-current border-t-transparent motion-safe:animate-spin ${className}`}
      aria-hidden="true"
    />
  );
}

// ── Loading ─────────────────────────────────────────────────────────────────
// Consistent pulse + label, replacing the mix of bare "Loading…" text and
// ad-hoc dots across tabs.
export function Loading({ label = 'Loading…' }) {
  return (
    <div className="py-12 flex flex-col items-center justify-center gap-3 text-on-surface-variant" role="status" aria-live="polite">
      <span className="w-3 h-3 rounded-full bg-primary/60 animate-pulse" aria-hidden="true" />
      <span className="text-ui">{label}</span>
    </div>
  );
}

// ── EmptyState ────────────────────────────────────────────────────────────────
// Warm empty state. Pass a short title + supportive helper line.
// Brand moment (Wave D, Iris's pick): a small eighth-note glyph above the
// title — an understated music-school touch on every "all clear" screen.
// Inline SVG stroked in soft teal (text-primary/25 = the brand teal at low
// opacity, on-brand per the teal/coral/cream palette — never gold), decorative
// only (aria-hidden). Kept quiet on purpose: this is a working tool.
export function EmptyState({ title = 'Nothing here', children, variant = 'page', className = '' }) {
  if (variant === 'card') {
    // Design Wave 3 card form: say what's clear (serif), then the last thing
    // done. Teal check = done. Used where a whole screen is "all clear".
    return (
      <section aria-label={typeof title === 'string' ? title : undefined} className={`rounded-card border border-outline-variant/70 bg-surface-container-lowest shadow-e1 px-5 py-8 text-center ${className}`}>
        <span aria-hidden="true" className="mx-auto mb-3 inline-flex w-11 h-11 items-center justify-center rounded-full bg-primary-fixed text-on-primary-fixed-variant">
          <svg viewBox="0 0 20 20" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 10.5l3.2 3.2L15 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <p className="m-0 font-headline text-2xl text-on-surface">{title}</p>
        {children}
      </section>
    );
  }
  return (
    <div className={`py-16 text-center text-on-surface-variant ${className}`}>
      <svg
        viewBox="0 0 24 24"
        className="w-7 h-7 mx-auto mb-3 text-primary/25"
        aria-hidden="true"
        fill="none"
      >
        {/* Eighth note: stem with a curved flag + filled notehead. */}
        <path
          d="M10.5 17.2V4.9c3 .9 4.7 2.7 4.7 5.2 0 1.1-.3 2.1-.9 3"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <ellipse cx="8.1" cy="17.4" rx="2.6" ry="2" fill="currentColor" />
      </svg>
      <p className="m-0 font-label text-on-surface">{title}</p>
      {children && <p className="mt-1 mb-0 text-ui">{children}</p>}
    </div>
  );
}

// ── ErrorState ────────────────────────────────────────────────────────────────
// Distinct from empty — an actual failure, with a Retry affordance. Tabs must
// never collapse a failed fetch into the empty state.
export function ErrorState({ message = "Couldn't load this — please try again.", onRetry, variant = 'page', className = '' }) {
  if (variant === 'inline') {
    // Design Wave 3 inline form for one card or list inside a page: one line,
    // "Try again" on the right, and the page around it keeps working.
    return (
      <div role="alert" className={`flex flex-wrap items-center justify-between gap-x-3 py-1 ${className}`}>
        <p className="m-0 py-2 text-ui text-on-surface">{message}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="min-h-11 px-2 -mr-2 font-label text-ui text-primary hover:underline underline-offset-2"
          >
            Try again
          </button>
        )}
      </div>
    );
  }
  return (
    <div className={`py-16 text-center text-on-surface-variant ${className}`} role="alert">
      <p className="mb-1">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex min-h-11 min-w-11 items-center justify-center px-3 text-primary underline underline-offset-2"
        >
          Try again
        </button>
      )}
    </div>
  );
}

// ── PageContainer + PageHeader ────────────────────────────────────────────────
// One page shell for every staff tab. Before Wave C, ~25 pages hand-rolled
// max-w-2xl/3xl/4xl/5xl/6xl containers and 6 different h1 recipes. Standard:
// content pages match the header/nav shell (max-w-5xl); `width="wide"`
// (max-w-6xl) is reserved for genuinely wide grids (Front Desk week grid, Time
// Off coverage planner); `width="narrow"` (max-w-2xl) for focused single-column
// flows (forms, quiz, today's-shift checklist) where full-width inputs or long
// prose lines would read worse.
const PAGE_WIDTHS = {
  narrow: 'max-w-2xl',
  default: 'max-w-5xl',
  wide: 'max-w-6xl',
};

export function PageContainer({ width = 'default', className = '', children }) {
  const w = PAGE_WIDTHS[width] || PAGE_WIDTHS.default;
  return (
    <div className={`${w} mx-auto px-4 sm:px-6 py-6 sm:py-10 ${className}`}>
      {children}
    </div>
  );
}

// Title + optional subtitle and actions slot (Refresh links, "+ Log issue",
// etc.). One h1 recipe portal-wide: font-headline text-2xl sm:text-3xl.
//
// Layout rule (2026-07-07 regression fix): the actions slot must NEVER widen
// the page or overlap the title. The old single-row `shrink-0` actions box
// sized at max-content — a wide slot (Front Desk's three buttons) crushed the
// title and forced body-level horizontal scroll on phones. Now: phones stack
// actions BELOW the title (flex-col), sm+ puts them beside it; the slot is
// shrinkable (min-w-0) and flex-wraps, so many buttons become extra rows
// instead of extra page width.
export function PageHeader({ title, subtitle, actions, className = '', heading = 'h1' }) {
  const Heading = heading === 'h2' ? 'h2' : 'h1';
  const headingClass = heading === 'h2'
    ? 'font-headline text-xl sm:text-2xl text-on-surface m-0 min-w-0 sm:flex-1'
    : 'font-headline text-2xl sm:text-3xl text-on-surface m-0 min-w-0 sm:flex-1';
  return (
    <header className={`mb-4 ${className}`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <Heading className={headingClass}>{title}</Heading>
        {actions && (
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0 sm:justify-end sm:pt-1.5">
            {actions}
          </div>
        )}
      </div>
      {subtitle && (
        <p className="text-on-surface-variant text-ui mt-1 mb-0 max-w-2xl">{subtitle}</p>
      )}
    </header>
  );
}

// ── StaffButton ───────────────────────────────────────────────────────────────
// Compact in-app button on brand tokens. Variants: primary (teal), danger
// (coral), ghost (text). Native <button> inherits the global focus-visible ring.
const VARIANTS = {
  primary: 'bg-primary text-on-primary hover:bg-primary/90',
  danger: 'bg-secondary text-on-secondary hover:bg-secondary/90',
  ghost: 'bg-transparent text-primary hover:bg-primary/5 border border-primary/30',
};
// "secondary" was never a variant, so Capacity's Refresh / Clear filters,
// Approvals' follow-up retry and Front Desk's "Review notifications" fell
// back to a FILLED teal button — breaking the one-filled-button-per-card rule
// (Design Wave 1). They meant the quiet outline (design review 2026-10-07).
VARIANTS.secondary = VARIANTS.ghost;

// min-h-11 (44px) at the base so every button is a full-size thumb target on
// touch screens — the primary users are receptionists on phones and tablets.
// `fine:min-h-0` lets desktop (sm+ with a mouse) keep the compact ~36px height
// (py-2 still sets it). Label stays text-sm for now: moving it to text-ui
// would widen every button row in the portal (pass 2).
// `press` (src/index.css) = the 120 ms 0.98 press; it only acts inside the
// staff portal and never under reduced motion.
const BUTTON_BASE =
  'press inline-flex items-center justify-center gap-1.5 rounded-full min-h-11 fine:min-h-0 px-4 py-2 text-sm font-label transition-colors';

export const StaffButton = React.forwardRef(function StaffButton(
  { variant = 'primary', type = 'button', className = '', children, ...rest },
  ref
) {
  const v = VARIANTS[variant] || VARIANTS.primary;
  return (
    <button
      ref={ref}
      type={type}
      className={`${BUTTON_BASE} disabled:opacity-50 disabled:cursor-not-allowed ${v} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
});

// ── FilterPill ────────────────────────────────────────────────────────────────
// Pill-shaped VALUE-filter control (status filters, scope chips, range presets,
// method pickers) with StaffButton's touch floor (min-h-11, compact again
// only for a desktop mouse). Selected = solid fill; `tone="danger"` renders the coral urgency
// variant (Approvals' Failed filter). Defaults to aria-pressed toggle
// semantics; pass an explicit `role` (radio / switch) + aria-checked via rest
// to opt into a different pattern — the default aria-pressed is dropped then.
const PILL_TONES = {
  primary: {
    on: 'bg-primary text-on-primary border-primary',
    off: 'bg-surface-container-low text-on-surface-variant border-outline-variant hover:text-on-surface',
  },
  // Coral urgent (Mark 2026-09-28) — FilterPill is staff-only, so the old
  // red `danger` fill moves to the same coral as StatusChip's urgent tone.
  danger: {
    on: 'bg-secondary text-on-secondary border-secondary',
    off: 'bg-surface-container-low text-secondary border-secondary/40 hover:bg-secondary/5',
  },
};

export function FilterPill({ active = false, tone = 'primary', className = '', children, ...rest }) {
  const t = PILL_TONES[tone] || PILL_TONES.primary;
  return (
    <button
      type="button"
      aria-pressed={rest.role ? undefined : active}
      className={`inline-flex items-center justify-center gap-1.5 font-label text-meta tracking-wide px-3 py-1.5 min-h-11 fine:min-h-0 rounded-full border transition-colors ${
        active ? t.on : t.off
      } ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

// ── SegmentedControl ──────────────────────────────────────────────────────────
// Joined two-to-four-way value switch (Time Off's Active / Show all scope).
// Same touch floor as FilterPill; plain buttons + aria-pressed (NOT ARIA tabs —
// there are no tabpanels and no arrow-key contract here).
export function SegmentedControl({ value, onChange, options, label, className = '' }) {
  return (
    <div
      role="group"
      aria-label={label}
      className={`flex rounded-lg border border-outline-variant overflow-hidden text-sm shrink-0 ${className}`}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className={`px-3 py-1.5 min-h-11 fine:min-h-0 font-label transition-colors ${
              active
                ? 'bg-primary text-on-primary'
                : 'bg-surface text-on-surface-variant hover:bg-surface-container'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// Link styled exactly like StaffButton — for navigations that read as buttons
// ("+ Log issue", "Take graduation quiz"). Keeps real <Link> semantics.
export function StaffButtonLink({ variant = 'primary', to, className = '', children, ...rest }) {
  const v = VARIANTS[variant] || VARIANTS.primary;
  return (
    <Link to={to} className={`${BUTTON_BASE} no-underline ${v} ${className}`} {...rest}>
      {children}
    </Link>
  );
}

// ── Icon ──────────────────────────────────────────────────────────────────────
// Small inline-SVG set drawn on currentColor, so an icon takes its color from
// the text around it (token colors only). Use these instead of emoji: emoji
// render in the device's own colors (⚠ ⚡ 🔔 come out yellow/amber, which the
// brand never uses) and read inconsistently to screen readers.
// Decorative by default (aria-hidden). Pass `label` when the icon carries
// meaning the surrounding text doesn't already say.
const ICON_PATHS = {
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  warn: <><path d="M12 3.5L2.5 20h19L12 3.5z" /><path d="M12 10v4.5M12 17.5h.01" /></>,
  lock: <><rect x="5" y="11" width="14" height="9.5" rx="2" /><path d="M8 11V8a4 4 0 018 0v3" /></>,
  bell: <><path d="M6 16.5V11a6 6 0 0112 0v5.5l1.5 2h-15l1.5-2z" /><path d="M10 20.5a2 2 0 004 0" /></>,
  calendar: <><rect x="3.5" y="5" width="17" height="15.5" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4" /></>,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5.5M12 7.5h.01" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7.5V12l3 2" /></>,
  note: <><path d="M6 3.5h8.5l3.5 3.5v13.5H6z" /><path d="M9 12h6M9 16h4" /></>,
  message: <path d="M4 5.5h16v11H9.5L4 20.5v-15z" />,
  link: <path d="M10 14a4 4 0 005.66 0l3-3a4 4 0 00-5.66-5.66l-1 1M14 10a4 4 0 00-5.66 0l-3 3a4 4 0 005.66 5.66l1-1" />,
  bolt: <path d="M13 2.5L4.5 13.5H11l-1 8 8.5-11H12l1-8z" />,
  stop: <><circle cx="12" cy="12" r="9" /><rect x="8.5" y="8.5" width="7" height="7" rx="1" /></>,
};
export const ICON_NAMES = Object.freeze(Object.keys(ICON_PATHS));

const ICON_SIZES = { xs: 'w-3.5 h-3.5', sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-6 h-6' };

export function Icon({ name, label, size = 'sm', className = '', ...rest }) {
  const paths = ICON_PATHS[name];
  if (!paths) return null;
  const a11y = label
    ? { role: 'img', 'aria-label': label }
    : { 'aria-hidden': true };
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
      data-icon={name}
      className={`inline-block shrink-0 ${ICON_SIZES[size] || ICON_SIZES.sm} ${className}`}
      {...a11y}
      {...rest}
    >
      {paths}
    </svg>
  );
}

// ── IconButton ────────────────────────────────────────────────────────────────
// Icon-only button (close a sheet, dismiss a row). Always 44×44 — the hit area
// is the whole square even when the glyph is small. `label` is required: it is
// the button's only accessible name. Pass `icon` (an Icon name) or children.
const ICON_BUTTON_VARIANTS = {
  ghost: 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface',
  primary: 'bg-primary text-on-primary hover:bg-primary/90',
};

export const IconButton = React.forwardRef(function IconButton(
  { label, icon, variant = 'ghost', type = 'button', className = '', children, ...rest },
  ref
) {
  if (!label && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error('IconButton needs a `label` (its only accessible name).');
  }
  const v = ICON_BUTTON_VARIANTS[variant] || ICON_BUTTON_VARIANTS.ghost;
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={rest.title ?? label}
      className={`press inline-flex items-center justify-center min-h-11 min-w-11 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${v} ${className}`}
      {...rest}
    >
      {icon ? <Icon name={icon} size="md" data-testid="icon-button-glyph" /> : children}
    </button>
  );
});

// ── TextLink ──────────────────────────────────────────────────────────────────
// A standalone text link (email, phone, "View profile →") with a 44px-tall tap
// area on touch screens. `to` renders a router <Link>; `href` a plain <a>
// (mailto:, tel:, external). For links inside running prose keep a plain
// inline <a> — a tall flex box would break the line.
const TEXT_LINK_BASE =
  'inline-flex items-center gap-1 min-h-11 fine:min-h-0 text-primary hover:underline underline-offset-2';

export const TextLink = React.forwardRef(function TextLink(
  { to, href, className = '', children, ...rest },
  ref
) {
  const cls = `${TEXT_LINK_BASE} ${className}`;
  if (to) {
    return <Link ref={ref} to={to} className={cls} {...rest}>{children}</Link>;
  }
  return <a ref={ref} href={href} className={cls} {...rest}>{children}</a>;
});

// ── Input / Select / Textarea ─────────────────────────────────────────────────
// Form fields on the staff tokens. 16px text on every touch screen, phones and
// iPads alike (iOS zooms the page when a focused field is under 16px —
// src/index.css backs this up for raw fields); the `ui` size only for a
// desktop mouse (`fine:`). 44px tall on touch, compact for a mouse.
// Width is the caller's (add w-full where a field should fill its row).
const FIELD_BASE =
  'rounded-control border border-outline-variant bg-surface text-base fine:text-ui text-on-surface ' +
  'placeholder:text-on-surface-variant focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 ' +
  'disabled:opacity-60 disabled:cursor-not-allowed';

export const Input = React.forwardRef(function Input({ type = 'text', className = '', ...rest }, ref) {
  return (
    <input ref={ref} type={type} className={`${FIELD_BASE} px-3 py-2 min-h-11 fine:min-h-0 ${className}`} {...rest} />
  );
});

export const Select = React.forwardRef(function Select({ className = '', children, ...rest }, ref) {
  return (
    <select ref={ref} className={`${FIELD_BASE} pl-3 pr-8 py-2 min-h-11 fine:min-h-0 ${className}`} {...rest}>
      {children}
    </select>
  );
});

export const Textarea = React.forwardRef(function Textarea({ rows = 3, className = '', ...rest }, ref) {
  return (
    <textarea ref={ref} rows={rows} className={`${FIELD_BASE} px-3 py-2 ${className}`} {...rest} />
  );
});

// ── Surfaces (re-exported) ────────────────────────────────────────────────────
// Card / Tile / StatTile / SectionLabel live in surfaces.jsx; import them from
// here like every other kit piece.
export { Card, Tile, StatTile, SectionLabel } from './surfaces';

// ── Toast ─────────────────────────────────────────────────────────────────────
// Bottom-center status toast with an optional action (e.g. Undo). Pages own the
// state; useToast() below manages the auto-dismiss timer. Matches Cancellations.
//
// Placement (2026-07-17): on phones (<640px) the fixed MobileTabBar owns the
// bottom 3.5rem (+ safe area), so the toast clears it — bottom-4 alone parked
// toasts (and their Undo buttons) underneath the bar. z-[75] keeps it above
// every overlay backdrop in the portal (QuickRef z-[60], mobile More sheet
// z-[65], command palette z-[70]) so a status/Undo is never dimmed out.
// Shared-toast context (2026-07-17): when one page composes several embedded
// sections that each call useToast() + render <Toast> (the Action Center hosts
// Approvals AND Tasks), two identical fixed portals used to overlap at
// bottom-center. Wrap the page in <SharedToastHost> and: useToast() in every
// descendant resolves to ONE shared state, the host renders the single Toast,
// and the sections' own <Toast> renders become no-ops. Standalone pages (no
// host) behave exactly as before.
const SharedToastContext = React.createContext(null);

export function Toast({ toast, onAction, onDismiss, host = false }) {
  // Under a SharedToastHost, only the host's own render shows — an embedded
  // section's <Toast> is a silent no-op (its toast state IS the shared one).
  const shared = React.useContext(SharedToastContext);
  if (shared && !host) return null;
  if (!toast) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      // max-w keeps a long server reason inside narrow viewports (466px Duo
      // outer screen) — it wraps instead of overflowing sideways.
      className="fixed bottom-[calc(3.5rem+env(safe-area-inset-bottom)+1rem)] sm:bottom-4 left-1/2 -translate-x-1/2 z-[75] flex items-center gap-4 w-max max-w-[calc(100vw-2rem)] rounded-3xl bg-on-surface text-surface px-4 py-2 text-ui shadow-lg motion-safe:animate-toast-in"
    >
      <span className="min-w-0">{toast.msg}</span>
      {toast.action && (
        <button
          type="button"
          // Default to the action fn carried on the toast itself — pages that
          // forget to wire onAction (Front Desk's Retry did) still work.
          onClick={() => { (onAction || toast.action.fn)?.(); onDismiss?.(); }}
          // 44px tap target on touch at every width; the negative margins
          // keep the pill's visual height unchanged.
          className="shrink-0 inline-flex items-center min-h-11 -my-3 px-2 -mx-2 font-label underline underline-offset-2"
        >
          {toast.action.label}
        </button>
      )}
    </div>
  );
}

// One toast slot for a page composed of embedded sections. Render around the
// sections; their useToast() calls share this state and the single portal here.
export function SharedToastHost({ children }) {
  const t = useToastState();
  return (
    <SharedToastContext.Provider value={t}>
      {children}
      <Toast host toast={t.toast} onAction={t.toast?.action?.fn} onDismiss={t.dismiss} />
    </SharedToastContext.Provider>
  );
}

// ── DoneBy ────────────────────────────────────────────────────────────────────
// One convention for "who did it, when", used across Tasks, Coverage, Front Desk
// time-off, Withdrawals, and Approvals. The four tables keep their own column
// names (resolved_by/at, decided_by/at, handled_by/at, claimed_by/executed_at) —
// this is a DISPLAY-only unifier: pass whichever pair a row carries. Renders
// "<verb> · 2h ago by Krystal", degrading gracefully when either half is absent.
//
//   <DoneBy by={t.resolved_by} at={t.resolved_at} verb="Done" />
//   <DoneBy by={t.handled_by} at={t.handled_at} verb="Handled" />
//   <DoneBy by={r.decided_by} at={r.decided_at} verb="Approved" />
//
// `prefix` prepends a leading checkmark for the check-off rows that want it.
export function DoneBy({ by, at, verb = 'Done', prefix = false, className = '' }) {
  if (!by && !at) return null;
  return (
    <span className={`font-label text-meta text-on-surface-variant ${className}`}>
      {prefix && <span className="text-primary mr-1" aria-hidden="true">✓</span>}
      {verb}
      {at && ` · ${timeAgo(at)}`}
      {by && ` by ${by}`}
    </span>
  );
}

// ── SyncFreshness ─────────────────────────────────────────────────────────────
// "Last synced X ago · Refresh" line for the mirror-backed tabs (PKA pushes
// Opus1 data to Neon on a schedule: Time Off, Invoices, Directory). Muted
// neutral while fresh; past `staleAfterMinutes` it switches to the warn tone
// (soft coral) plus an explicit "sync may be stale" suffix — the words carry
// the meaning, color is never the only signal. Replaces three hand-rolled
// copies that had drifted onto off-token colors (text-orange-700 /
// text-secondary). No sync on record counts as stale (data is missing).
// Staleness is computed at render time — these tabs already re-render on
// their own refetch cycles (mount, visibility refetch, Refresh tap).
//
// WINDOW-AWARE staleness (2026-07-17): the schedules only run during business
// hours (e.g. Time Off syncs every ~30 min, 8a–9p ET), so a flat
// "older than 90 min = stale" fired the coral "sync may be stale" banner EVERY
// night — the 9pm sync is correctly the last of the day, but by 11pm it read as
// broken. Pass `syncWindow={{ startHour, endHour }}` (24h ET) to make staleness
// window-aware: warn only when a sync was MISSED inside the active window, or
// past a hard `absoluteStaleHours` ceiling (default 14h). Outside the window a
// fresh sync shows a quiet "Synced through <endHour>" instead of a false alarm.
// Omit `syncWindow` (Invoices/Directory today) to keep the flat behavior.
function minutesOfDayInTz(d, tz) {
  // en-GB gives 24h "HH:MM"; parse to minutes-of-day in the target zone.
  const hhmm = d.toLocaleTimeString('en-GB', { timeZone: tz, hour12: false, hour: '2-digit', minute: '2-digit' });
  const [h, m] = hhmm.split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
}
function hourLabel(hour24) {
  const h12 = ((hour24 + 11) % 12) + 1;
  return `${h12}:00 ${hour24 < 12 ? 'AM' : 'PM'}`;
}
// TICKING (2026-08-03 audit P2-15): the "x ago" string used to be computed at
// render time only, so an idle tab could read "27 min ago" for hours. A 30s
// interval re-renders while a sync timestamp exists. `cadenceLabel` (optional)
// states the update cadence and shows the absolute ET time, so "27 min ago" on
// a ~30-min mirror reads as NORMAL instead of as breakage.
export function SyncFreshness({
  syncedAt, staleAfterMinutes, onRefresh,
  label = 'Awaiting first sync from Opus1',
  loadingLabel = 'Checking last Opus1 sync…',
  loading = false,
  syncOk = true,
  className = '',
  syncWindow = null,
  cadenceLabel = null,
  refreshBusy = false,
}) {
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    if (!syncedAt) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [syncedAt]);
  const ageMin = syncedAt ? Math.floor((Date.now() - new Date(syncedAt).getTime()) / 60000) : Infinity;

  let stale;
  let quietOutsideWindow = false;
  if (!syncWindow) {
    stale = !(ageMin <= staleAfterMinutes); // NaN (unparseable ts) counts as stale
  } else {
    const { startHour = 8, endHour = 21, tz = 'America/New_York', absoluteStaleHours = 14 } = syncWindow;
    const nowMin = minutesOfDayInTz(new Date(), tz);
    const withinWindow = nowMin != null && nowMin >= startHour * 60 && nowMin <= endHour * 60;
    const absStale = !(ageMin <= absoluteStaleHours * 60); // NaN also trips this
    // Missed a scheduled sync WHILE the window is open, or blown the hard
    // ceiling → genuinely stale. Otherwise (evening/overnight) it's expected.
    stale = absStale || (withinWindow && ageMin > staleAfterMinutes);
    quietOutsideWindow = !stale && !withinWindow && Boolean(syncedAt);
  }

  const endHour = syncWindow ? (syncWindow.endHour ?? 21) : 21;
  // Absolute ET clock time of the last sync — "updated 10:42 AM ET" is a fact
  // a reader can trust on a frozen tab; a bare relative string is not.
  const atET = syncedAt
    ? new Date(syncedAt).toLocaleTimeString('en-US', {
      timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit',
    })
    : null;
  let text;
  if (!syncedAt && loading) text = loadingLabel;
  else if (!syncedAt) text = label;
  else if (syncOk === false) text = `Last successful Opus1 sync ${atET ? `${atET} ET` : timeAgo(syncedAt)} · last push reported a failure`;
  else if (stale) text = `Last synced ${timeAgo(syncedAt)} · sync may be stale`;
  else if (quietOutsideWindow) text = `Synced through ${hourLabel(endHour)}`;
  else if (cadenceLabel) text = `Opus1 last synced ${atET} ET (${timeAgo(syncedAt)}) · ${cadenceLabel}`;
  else text = `Opus1 last synced ${timeAgo(syncedAt)}`;

  return (
    <div className={`flex items-center justify-between gap-3 text-meta ${className}`}>
      <span className={`min-w-0 ${stale ? 'text-on-warn-container' : 'text-on-surface-variant'}`}>
        {text}
      </span>
      {onRefresh && (
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshBusy}
          className="min-h-11 min-w-11 inline-flex items-center justify-center text-primary hover:underline font-label shrink-0 disabled:no-underline disabled:opacity-60"
        >
          {refreshBusy ? 'Refreshing…' : 'Refresh'}
        </button>
      )}
    </div>
  );
}

// ── ProgressBar ───────────────────────────────────────────────────────────────
// Slim done/total bar with a "8 / 12 · 67%" label. Teal fill on a neutral track,
// width transitions on change. `compact` trims the label for dense lists. Renders
// nothing when total is 0. Extracted from the Time Off coverage planner so any
// tab that needs a completion bar reads the same. Pass a { total, done, pct }
// object where pct is an integer 0–100.
export function ProgressBar({ progress, compact = false }) {
  const { total, done, pct } = progress;
  if (total === 0) return null;
  const complete = pct === 100;
  // Ask-lane honesty (2026-08-03 audit P2-16): an ask-card pct means "asks
  // SENT", not "coverage resolved" — the resolution claim belongs to the
  // lifecycle evidence bar. A full ask bar therefore says what is true
  // ("All asks sent") instead of "All clear 🎉".
  const askLane = Boolean(progress.askLane);
  return (
    <div className={compact ? 'mt-2' : 'mt-3'}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="font-label text-meta text-on-surface-variant tabular-nums">
          {done} / {total}
          <span className="mx-1" aria-hidden="true">·</span>
          <span className={complete ? 'text-primary font-medium' : ''}>{pct}%</span>
        </span>
        {!compact && complete && (
          <span className="font-label text-meta text-primary">
            {askLane ? 'All messages sent' : 'Checklist complete'}
          </span>
        )}
      </div>
      <div
        className="h-1.5 w-full rounded-full bg-surface-container overflow-hidden"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${done} of ${total} ${askLane ? 'messages sent' : 'checklist items complete'}, ${pct} percent`}
      >
        <div
          className={`h-full rounded-full transition-[width] duration-500 ease-out ${
            complete ? 'bg-primary' : 'bg-primary/80'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// useToast — toast state + auto-dismiss. showToast(msg, actionFn?, actionLabel?).
// Resolves to the page's SharedToastHost state when one is mounted above,
// otherwise local state (both hooks always run — hooks rules).
export function useToast() {
  const shared = React.useContext(SharedToastContext);
  const local = useToastState();
  return shared || local;
}

function useToastState() {
  const [toast, setToast] = React.useState(null);
  const timer = React.useRef(null);
  const show = React.useCallback((msg, actionFn, actionLabel = 'Undo') => {
    if (timer.current) clearTimeout(timer.current);
    setToast({ msg, action: actionFn ? { label: actionLabel, fn: actionFn } : null });
    // Undo-bearing toasts live exactly as long as the shared undo window
    // (usePendingCommit) — a 6s toast around a 5s window left "Undo" tappable
    // for a second after the commit had already fired. Other toasts (plain
    // status, Retry) keep the roomier 6s.
    const ms = actionFn && actionLabel === 'Undo' ? UNDO_WINDOW_SECONDS * 1000 : 6000;
    timer.current = setTimeout(() => setToast(null), ms);
  }, []);
  const dismiss = React.useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    setToast(null);
  }, []);
  React.useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return { toast, show, dismiss };
}
